import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import * as sessionsApi from '../../api/sessions';
import type { Department, Location } from '../../api/organizations';
import { extractErrorMessage } from '../../api/errors';
import type { TimelineEvent } from '../../components/CaptureTimeline';
import { SearchableSelect } from '../../components/SearchableSelect';

const NONE = [{ value: '', label: '— none —' }];
const KIND_OPTIONS = sessionsApi.SESSION_KINDS.map((k) => ({
  value: k as string,
  label: sessionsApi.SESSION_KIND_LABELS[k],
}));

/**
 * Tag a recording with its department / location / kind / external case ref
 * (RFC procedure-instance, Phase 1b). Extracted from TenantActivity so the
 * form owns its own draft state + save; the page just says which event is
 * open (`event`, null = closed) and reloads on success.
 *
 * The form seeds from the event each time one opens, so re-tagging shows the
 * current values. Departments + locations are passed in (the page already
 * loads them for the feed filters and name resolution).
 */
export function SessionTagDialog({
  event,
  tenantId,
  departments,
  locations,
  onClose,
  onSaved,
}: {
  event: TimelineEvent | null;
  tenantId: string;
  departments: Department[];
  locations: Location[];
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [dept, setDept] = useState('');
  const [loc, setLoc] = useState('');
  const [kind, setKind] = useState('');
  const [caseRef, setCaseRef] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (event) {
      setDept(event.departmentId ?? '');
      setLoc(event.locationId ?? '');
      setKind(event.sessionKind ?? '');
      setCaseRef(event.externalCaseRef ?? '');
      setError(null);
    }
  }, [event]);

  const submit = async () => {
    if (!event) return;
    setSaving(true);
    setError(null);
    try {
      await sessionsApi.tagCaptureSession(tenantId, event.id, {
        department_id: dept || null,
        location_id: loc || null,
        kind: kind || null,
        external_case_ref: caseRef.trim() || null,
      });
      onClose();
      await onSaved();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to tag session'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={event !== null}
      onClose={() => (saving ? null : onClose())}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>Department &amp; location</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Tag this recording with where it happened, so activity can be
          organized and compared.
        </DialogContentText>
        <Stack spacing={2}>
          <SearchableSelect
            label="Department"
            fullWidth
            value={dept}
            onChange={setDept}
            disabled={saving}
            pinned={NONE}
            options={departments.map((d) => ({ value: d.id, label: d.name }))}
          />
          <SearchableSelect
            label="Location"
            fullWidth
            value={loc}
            onChange={setLoc}
            disabled={saving}
            pinned={NONE}
            options={locations.map((l) => ({ value: l.id, label: l.name }))}
          />
          <SearchableSelect
            label="Kind"
            fullWidth
            value={kind}
            onChange={setKind}
            disabled={saving}
            pinned={NONE}
            options={KIND_OPTIONS}
          />
          <TextField
            label="External case reference (optional)"
            fullWidth
            value={caseRef}
            onChange={(e) => setCaseRef(e.target.value)}
            disabled={saving}
            helperText="An opaque case number to correlate later — no patient info."
          />
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void submit()} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
